import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { dashboardSpec } from '@holymoly/dashboards';
import { DashboardGrid } from '@/components/dashboards/DashboardGrid';
import { VariablesBar } from '@/components/dashboards/VariablesBar';
import type { VariableValues } from '@/components/dashboards/Widget';
import { PageTitle } from '@/components/PageTitle';
import { errorText } from '@/lib/errors';
import { requirePermission } from '@/lib/guards';
import { orpc } from '@/lib/orpc';
import { Link } from '@/lib/router-compat';

export const Route = createFileRoute('/_app/dashboards/$dashboardId/')({
  // Filters live in the URL: a filtered dashboard can be shared as a link.
  validateSearch: z.object({ vars: z.string().max(4000).optional(), version: z.number().int().positive().optional() }),
  beforeLoad: ({ context }) => requirePermission(context.me, 'dashboards.view'),
  component: DashboardPage
});

function parseVars(s: string | undefined): VariableValues {
  if (!s) return {};
  try {
    const v = JSON.parse(s) as unknown;
    return v && typeof v === 'object' ? (v as VariableValues) : {};
  } catch {
    return {};
  }
}

function DashboardPage() {
  const { t } = useTranslation();
  const { dashboardId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const d = useQuery(orpc.dashboards.get.queryOptions({ input: { id: dashboardId, ...(search.version && { version: search.version }) } }));
  const values = parseVars(search.vars);

  if (d.isError) return <div className="p-6 text-sm">{errorText(t, d.error)}</div>;
  if (!d.data) return <div className="skeleton m-6 h-96" />;
  const parsed = dashboardSpec.safeParse(d.data.spec);
  const data = d.data;

  return (
    <div className="space-y-4 p-6">
      <PageTitle
        title={data.title}
        items={[{ label: t('dashboards.title'), path: '/dashboards' }, { label: data.title, active: true }]}
        actions={
          data.access !== 'viewer' && (
            <Link to={`/dashboards/${data.id}/edit`} className="btn btn-sm btn-outline border-base-300">
              <span className="iconify lucide--pencil size-4" />
              {t('dashboards.edit')}
              {data.pendingProposals > 0 && <span className="badge badge-info badge-xs">{data.pendingProposals}</span>}
            </Link>
          )
        }
      />
      {data.status !== 'published' && (
        <div className="alert alert-warning alert-soft text-sm">
          {t('dashboards.viewingVersion', { version: data.version, status: t(`dashboards.statuses.${data.status}`) })}
          {data.publishedVersion !== null && (
            <button type="button" className="btn btn-xs btn-ghost" onClick={() => void navigate({ search: (s) => ({ ...s, version: undefined }) })}>
              {t('dashboards.showPublished')}
            </button>
          )}
        </div>
      )}
      {parsed.success ? (
        <>
          {parsed.data.description && <p className="text-base-content/70 text-sm">{parsed.data.description}</p>}
          <VariablesBar
            variables={parsed.data.variables}
            values={values}
            onChange={(next) => void navigate({ search: (s) => ({ ...s, vars: JSON.stringify(next) }), replace: true })}
          />
          <DashboardGrid spec={parsed.data} values={values} />
        </>
      ) : (
        <div className="alert alert-error alert-soft text-sm">{t('dashboards.loadError')}</div>
      )}
    </div>
  );
}
